import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  AlertCircle,
  BookMarked,
  ChevronRight,
  Search,
} from "lucide-react";
import { DX } from "@/constants/testIds";
import {
  getDiagnosisCodeById,
  listDiagnosisCodeVersions,
  pickCurrentVersion,
  searchDiagnosisCodesPage,
} from "@/lib/diagnosisApi";
import DiagnosisDetailDialog from "@/components/diagnosis/DiagnosisDetailDialog";
import "@/styles/emr-catalog.css";

const PAGE_SIZE = 25;

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return "Unable to load ICD-10-CM data.";
}

export default function Icd10CmPage() {
  const [versions, setVersions] = useState([]);
  const [versionYear, setVersionYear] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("active");
  const [billableOnly, setBillableOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setVersionsLoading(true);
      try {
        const data = await listDiagnosisCodeVersions();
        if (cancelled) return;
        const list = data.items || [];
        setVersions(list);
        const current = pickCurrentVersion(data);
        setVersionYear(current?.versionYear ?? list[0]?.versionYear ?? null);
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e));
      } finally {
        if (!cancelled) setVersionsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, versionYear, activeFilter, billableOnly]);

  const loadResults = useCallback(async () => {
    if (!debouncedSearch || versionYear == null) {
      setItems([]);
      setHasMore(false);
      setCapped(false);
      setError(null);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);
    setHasSearched(true);
    try {
      const result = await searchDiagnosisCodesPage({
        q: debouncedSearch,
        versionYear,
        billable: billableOnly ? true : undefined,
        active: activeFilter === "active" ? true : false,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(result.items);
      setHasMore(result.hasMore);
      setCapped(result.capped);
    } catch (e) {
      setItems([]);
      setHasMore(false);
      setCapped(false);
      setError(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, versionYear, activeFilter, billableOnly, page]);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetailTarget(row);
    setDetailLoading(true);
    try {
      const full = await getDiagnosisCodeById(row.id);
      setDetailTarget(full);
    } catch {
      setDetailTarget(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const selectedVersion = versions.find((v) => v.versionYear === versionYear);
  const showInitialPrompt = !debouncedSearch && !loading && !error;
  const showEmpty = hasSearched && !loading && !error && items.length === 0;

  return (
    <div data-testid={DX.page} className="emr-catalog emr-catalog--full-table">
      <div className="emr-catalog__pagehead">
        <div>
          <h1 data-testid={DX.title} className="emr-catalog__title">
            ICD-10-CM
          </h1>
          <p className="emr-catalog__subtitle">
            Official CMS/NCHS diagnosis terminology — reference lookup only
          </p>
        </div>
      </div>

      <div className="emr-catalog__workspace">
        <section
          data-testid={DX.banner}
          className="card emr-catalog__section emr-catalog__section--notice"
        >
          <h2 className="emr-catalog__section-title">Terminology / reference data</h2>
          <p className="emr-catalog__copy">
            Search and browse official ICD-10-CM codes here for coding reference.
            Selecting or viewing a code on this page does{" "}
            <span className="emr-catalog__name">not</span> create a patient diagnosis or
            clinical record.
          </p>
        </section>

        <section className="card emr-catalog__section">
          <h2 className="emr-catalog__section-title">Search &amp; filters</h2>
            <div className="emr-catalog__filters emr-catalog__filters--search-row">
              <div className="emr-catalog__search">
                <Search className="emr-catalog__search-icon" aria-hidden="true" />
                <input
                  data-testid={DX.search}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by ICD-10 code (e.g. M54.50) or diagnosis description…"
                  className="emr-catalog__input"
                  autoFocus
                />
              </div>
              <div className="emr-catalog__field">
                <span>Version / Year</span>
                <Select
                  value={versionYear != null ? String(versionYear) : undefined}
                  onValueChange={(v) => setVersionYear(Number(v))}
                  disabled={versionsLoading || !versions.length}
                >
                  <SelectTrigger id="version-filter" data-testid={DX.versionFilter} className="emr-catalog__filter-trigger">
                    <SelectValue placeholder="Select version" />
                  </SelectTrigger>
                  <SelectContent className="emr-catalog-select">
                    {versions.map((v) => (
                      <SelectItem key={v.id} value={String(v.versionYear)}>
                        {v.versionLabel || `FY${v.versionYear}`}
                        {v.isCurrent ? " (current)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="emr-catalog__field">
                <span>Active</span>
                <Select value={activeFilter} onValueChange={setActiveFilter}>
                  <SelectTrigger id="active-filter" data-testid={DX.activeFilter} className="emr-catalog__filter-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="emr-catalog-select">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="emr-catalog__switch">
                <Switch
                  id="billable-filter"
                  data-testid={DX.billableFilter}
                  checked={billableOnly}
                  onCheckedChange={setBillableOnly}
                />
                <label htmlFor="billable-filter">Billable only</label>
              </div>
            </div>
            {selectedVersion?.recordCounts && (
              <p className="emr-catalog__meta">
                {selectedVersion.versionLabel}:{" "}
                {selectedVersion.recordCounts.total?.toLocaleString()} codes imported
                {selectedVersion.recordCounts.billable != null &&
                  ` (${selectedVersion.recordCounts.billable.toLocaleString()} billable)`}
              </p>
            )}
        </section>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">ICD-10-CM codes</h2>
          {versionsLoading ? (
            <div className="emr-catalog__status-msg">Loading versions…</div>
          ) : error ? (
            <div data-testid={DX.errorState} className="emr-catalog__error">
              <AlertCircle className="emr-catalog__error-icon" aria-hidden="true" />
              <p className="emr-catalog__error-copy">{error}</p>
              <Button variant="outline" className="btn emr-catalog__retry" onClick={loadResults}>
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div data-testid={DX.loading} className="emr-catalog__status-msg">
              Searching ICD-10-CM codes…
            </div>
          ) : showInitialPrompt ? (
            <div className="emr-catalog__empty">
              <BookMarked className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>Enter an ICD-10 code or diagnosis description above to search the master list.</p>
            </div>
          ) : showEmpty ? (
            <div data-testid={DX.emptyState} className="emr-catalog__empty">
              <Search className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>
                No ICD-10-CM codes match &ldquo;{debouncedSearch}&rdquo;
                {billableOnly ? " (billable only)" : ""}
                {activeFilter === "inactive" ? " (inactive)" : ""}.
              </p>
            </div>
          ) : (
            <>
              <div className="emr-catalog__table-wrap">
                <table data-testid={DX.table} className="striped highlight emr-catalog__table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Official Description</th>
                      <th>Billable</th>
                      <th>Active</th>
                      <th>Version</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => (
                      <tr
                        key={row.id}
                        data-testid={`${DX.row}-${row.id}`}
                        className="cursor-pointer"
                        onClick={() => openDetail(row)}
                      >
                        <td className="font-fhir emr-catalog__name">
                          {row.code || row.codeNormalized}
                        </td>
                        <td>{row.description}</td>
                        <td>
                          {row.isBillable ? (
                            <span className="emr-catalog__status emr-catalog__status--active">Yes</span>
                          ) : (
                            <span className="emr-catalog__status emr-catalog__status--inactive">No</span>
                          )}
                        </td>
                        <td>
                          {row.active !== false ? (
                            <span className="emr-catalog__status emr-catalog__status--active">Yes</span>
                          ) : (
                            <span className="emr-catalog__status emr-catalog__status--inactive">No</span>
                          )}
                        </td>
                        <td className="emr-catalog__muted">
                          {row.versionLabel || (row.versionYear ? `FY${row.versionYear}` : "—")}
                        </td>
                        <td>
                          <ChevronRight className="h-4 w-4 emr-catalog__muted" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(page > 1 || hasMore) && (
                <div data-testid={DX.pagination} className="emr-catalog__pager">
                  <p>
                    Page {page}
                    {items.length > 0 &&
                      ` · showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + items.length}`}
                  </p>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          data-testid={DX.prevPage}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (page > 1) setPage((p) => p - 1);
                          }}
                          className={page <= 1 ? "pointer-events-none opacity-50" : undefined}
                        />
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationNext
                          data-testid={DX.nextPage}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            if (hasMore) setPage((p) => p + 1);
                          }}
                          className={!hasMore ? "pointer-events-none opacity-50" : undefined}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}

              {capped && (
                <p data-testid={DX.cappedNotice} className="emr-catalog__cap">
                  Showing the first 100 matching codes. Refine your search for more specific results.
                </p>
              )}
            </>
          )}
        </section>
      </div>

      <DiagnosisDetailDialog
        open={detailOpen}
        diagnosis={detailTarget}
        loading={detailLoading}
        onClose={() => {
          setDetailOpen(false);
          setDetailTarget(null);
        }}
      />
    </div>
  );
}
